import { GAME_IDS, gamePathOf, similarGames, type GameId, type LimitsState } from '@tg/shared';

/**
 * «Try another» (owner decision 2026-10-01): after a session in a game — leaving its screen after ≥ 5 rounds, or with
 * the game's own back button after at least one — a quiet hint with 1–2 games of a similar mood the player has not
 * visited. Never on a game screen (so never during a round), at most once per 30 min, one tap closes it, and never
 * while a break or a daily limit of responsible play is on. No pressure: no stakes, no timers, no «last chance».
 */
export const TRY_MORE_ROUNDS = 5;
export const TRY_MORE_GAP_MS = 30 * 60_000;
export const TRY_MORE_MAX = 2;
const KEY = 'tg.tryMore.at';

/** game path below /:lang (@tg/shared gamePathOf: /<original>, /slots/<slot>, /puzzles/<puzzle>, /strategy/chess) → game */
const BY_PATH = new Map<string, GameId>(GAME_IDS.map((g) => [gamePathOf(g), g]));

/** the game whose screen `pathname` is, else null */
export function gameOfPath(pathname: string): GameId | null {
  const p = pathname.split('/').filter(Boolean);
  return BY_PATH.get('/' + p.slice(1).join('/')) ?? null;
}

/* the game's back button (GameShell) marks the exit as deliberate */
let byButton = false;
export function markExitByButton(): void {
  byButton = true;
}
/** reads and resets the mark */
export function takeExitByButton(): boolean {
  const v = byButton;
  byButton = false;
  return v;
}

/** per-device convenience: when the hint was last shown (0 — never / storage blocked) */
export function lastShownAt(): number {
  try {
    const v = Number(localStorage.getItem(KEY));
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}
export function rememberShown(now: number): void {
  try {
    localStorage.setItem(KEY, String(now));
  } catch {
    /* storage blocked: the gap lives in memory of this page only */
  }
  memo = now;
}
let memo = 0;

/** a break or any daily limit reached: no suggestion to play something else */
export function limitsBlock(l: LimitsState, now: number): boolean {
  if (l.breakUntil && Date.parse(l.breakUntil) > now) return true;
  if (l.tokensPerDay !== null && l.tokensUsedToday >= l.tokensPerDay) return true;
  if (l.minutesPerDay !== null && l.minutesUsedToday >= l.minutesPerDay) return true;
  return false;
}

export interface SessionEnd {
  from: GameId;
  rounds: number;
  byButton: boolean;
}

/** the games to suggest after a session, or [] when the hint must not show (yet) */
export function tryMorePick(end: SessionEnd, stamped: ReadonlySet<string> | null, now: number): GameId[] {
  if (!stamped) return [];
  if (end.rounds < (end.byButton ? 1 : TRY_MORE_ROUNDS)) return [];
  if (now - Math.max(lastShownAt(), memo) < TRY_MORE_GAP_MS) return [];
  return similarGames(end.from, new Set([...stamped, end.from]), TRY_MORE_MAX);
}

/** tests */
export function resetTryMore(): void {
  memo = 0;
  byButton = false;
}
